import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import type { ReactNode } from "react";
import "./design/global.css";
import { AuthProvider } from "./auth/AuthProvider.js";
import { PageChromeProvider } from "./design/pageChrome.js";
import { linkedReportCurrency, ReportCurrencyProvider } from "./format/reportCurrency.js";
import type { AuthState } from "./auth/principal.js";
import type { AppRouter } from "./router.js";

export interface AppProps {
  router: AppRouter;
  queryClient: QueryClient;
  /** Tests inject a principal; production uses the provider's own state. */
  auth?: AuthState;
}

/**
 * Provider order matters: the root route's shell renders the signed-in
 * identity, the current screen's title and the report-currency switch, so
 * auth, the page-chrome store and the report currency all have to be
 * established above the router rather than inside a route.
 *
 * There is no theme provider. Styling is CSS custom properties on `:root`
 * (`design/tokens.css`) plus CSS Modules, so nothing has to be in React's tree
 * for a component to know what a border colour is.
 */
export function App({ router, queryClient, auth }: AppProps): ReactNode {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider {...(auth === undefined ? {} : { value: auth })}>
        <PageChromeProvider>
          <ReportCurrencyProvider
            initial={linkedReportCurrency({
              pathname: router.state.location.pathname,
              search: router.state.location.search as Record<string, unknown>,
            })}
          >
            <RouterProvider router={router} />
          </ReportCurrencyProvider>
        </PageChromeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
